import type { Zona } from '../../api/ops/pagos';
import type { TarjetaAyuda } from '../../marco/cabecera';

/**
 * Textos de 8.5 Zonas, turnos y recursos (`diseno/reservas-sala/8.5-8.8-8.10-recursos-cierres-reservados.html`): G36
 * (tipos y capacidad con fecha de efecto), G214 y G658 (cómo llama el negocio a lo que se reserva), G245 (el horario de
 * la zona se enseña y se cambia en Horario, 3.1), G403 (los recursos existen sin el bloque Plano y sala), G404
 * (asignación al reservar o en sala), G405 («El cliente puede elegir») y G797 (`policy_name`, para quien no gestiona
 * políticas).
 */
export const PERMISOS_RECURSOS = {
    ver: 'reservas.configuracion.ver',
    gestionar: 'reservas.configuracion.gestionar',
    horario: 'disponibilidad.horarios.ver',
    cierres: 'reservas.cierres.gestionar',
} as const;

export const MODALIDAD: Readonly<Record<string, string>> = { slot: 'Franja', shift: 'Turnos', stay: 'Estancia' };

export const ASIGNACION: Readonly<Record<string, string>> = { at_sale: 'Al reservar', on_floor: 'En sala' };

export const TIPO_RECURSO: Readonly<Record<string, string>> = {
    table: 'Mesa',
    private_area: 'Reservado',
    sunbed: 'Hamaca',
    room: 'Habitación',
    bungalow: 'Bungaló',
    pitch: 'Parcela',
    meeting_room: 'Sala',
    other: 'Otro',
};

export const PRECIO: Readonly<Record<string, string>> = {
    per_person: 'por persona',
    per_reservation: 'por reserva',
    per_night: 'por noche',
    per_resource: 'por recurso',
};

export const DIAS_SEMANA = ['L', 'M', 'X', 'J', 'V', 'S', 'D'] as const;

/** «Turnos: Comida y Cena», «Franja de 2 h» o «Estancia de 2 a 7 noches». */
export function lineaDeZona(z: Zona): string {
    if (z.modality === 'shift') {
        const turnos = (z.shifts ?? []).map((t) => t.name);
        return turnos.length === 0 ? 'Turnos (sin turnos todavía)' : `Turnos: ${turnos.join(', ')}`;
    }
    if (z.modality === 'stay') {
        return `Estancia de ${String(z.min_nights ?? 1)} a ${String(z.max_nights ?? z.min_nights ?? 1)} noches`;
    }
    const min = z.default_duration_minutes ?? 0;
    const h = Math.floor(min / 60);
    const m = min % 60;
    const dur = min === 0 ? '' : ` de ${h > 0 ? `${String(h)} h` : ''}${h > 0 && m > 0 ? ' ' : ''}${m > 0 ? `${String(m)} min` : ''}`;
    return `Franja${dur}${z.public === false ? ' • solo en el panel' : ''}`;
}

export const AYUDA: TarjetaAyuda[] = [
    { icono: 'layers', titulo: 'Zonas', texto: 'Cada zona es lo que se reserva: un salón, una terraza, los reservados VIP. Tiene su aforo, su precio y su forma de cobro.' },
    { icono: 'layout-grid', titulo: 'Recursos', texto: 'Las mesas, reservados o habitaciones de cada zona, con su capacidad. Existen aunque no tengas el plano.' },
    { icono: 'clock', titulo: 'Horario', texto: 'El horario de cada zona se ve aquí y se cambia en Horario.' },
];
